// THE PARTY PAGE'S WALLETS TAB (UI rebuild batch 2) — screens/PartyDetail.tsx's WalletsSection in a v2 Panel, its logic
// copied whole: add a wallet (chain, address, label; "retire the other active wallets on this chain" OFF by default and
// offered only when there is one — sweep item 37), the Ledger-key line, in use and held first, retired (still watched) and
// archived (not watched) folded, a state pill only when NOT in use, KYT's risk figure as information, the Wallet register
// door. The Save button is a secondary: the page's one gold primary is Record money.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type PartyDetail } from '../../../lib/api';
import { errorWords } from '../../../lib/errors';
import { fmtRelative } from '../../../lib/format';
import { tg } from '../../../lib/telegram';
import { WALLET_CHAINS, chainIsRead, walletState, watchWords } from '../../../lib/walletStates';
import { Field } from '../../Field';
import { ErrorLine, Pill } from '../../kit';
import { Panel, EmptyState, StateLine, TAP } from '../kit';

type PartyWallet = PartyDetail['wallets'][number];
type PartyWalletKey = NonNullable<PartyDetail['wallet_keys']>[number];

export function PartyWallets({ partySlug, wallets, keys = [] }: { partySlug: string; wallets: PartyWallet[]; keys?: PartyWalletKey[] }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [chain, setChain] = useState<typeof WALLET_CHAINS[number]>('TRC20');
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  const [retirePrev, setRetirePrev] = useState(false);
  const othersOnChain = wallets.filter((w) => w.status === 'active' && w.chain === chain).length;
  const willRetire = retirePrev && othersOnChain > 0;

  const add = useMutation({
    mutationFn: () => api.addPartyWallet(partySlug, { chain, address, label: label || undefined, retire_previous_on_chain: willRetire }),
    onSuccess: () => {
      tg()?.HapticFeedback.notificationOccurred('success');
      setAdding(false);
      setAddress(''); setLabel('');
      qc.invalidateQueries({ queryKey: ['party', partySlug] });
    },
    onError: () => tg()?.HapticFeedback.notificationOccurred('error'),
  });

  const current = wallets.filter((w) => w.status === 'active' || w.status === 'flagged');
  const retired = wallets.filter((w) => w.status === 'archived');
  const archived = wallets.filter((w) => w.status === 'unwatched');

  return (
    <Panel
      title="Wallets"
      aside={current.length > 0 ? current.length : undefined}
      action={(
        <button type="button" onClick={() => setAdding((v) => !v)} className="btn-secondary min-h-11 px-3 text-body lg:min-h-10" aria-expanded={adding}>
          {adding ? 'Cancel' : 'Add wallet'}
        </button>
      )}
    >
      {adding && (
        <div className="mb-4 space-y-3 border-b border-stroke-soft pb-4">
          <div className="grid grid-cols-4 gap-2" role="group" aria-label="Chain">
            {WALLET_CHAINS.map((c) => (
              <button key={c} type="button" aria-pressed={chain === c} onClick={() => setChain(c)} className={`seg text-body ${chain === c ? 'seg-on' : ''}`}>{c}</button>
            ))}
          </div>
          <Field label="Address">
            <input className="input w-full font-mono text-field lg:text-body" value={address} onChange={(e) => setAddress(e.target.value)} />
          </Field>
          <Field label="Label" hint="optional">
            <input className="input w-full text-field lg:text-body" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="May 2026 rotation" />
          </Field>
          {othersOnChain > 0 && (
            <label className="flex min-h-11 items-center gap-2 text-body lg:min-h-10">
              <input type="checkbox" checked={retirePrev} onChange={(e) => setRetirePrev(e.target.checked)} />
              <span className="text-tg-hint">Retire the other active {chain} wallet{othersOnChain === 1 ? '' : 's'} ({othersOnChain})</span>
            </label>
          )}
          <button type="button" className="btn-secondary w-full text-body" disabled={!address.trim() || add.isPending} onClick={() => add.mutate()}>
            {add.isPending ? 'Saving…' : willRetire ? `Save and retire ${othersOnChain} wallet${othersOnChain === 1 ? '' : 's'}` : 'Save wallet'}
          </button>
          <ErrorLine>{add.isError ? errorWords(add.error) : null}</ErrorLine>
        </div>
      )}

      {current.length === 0 && retired.length === 0 && archived.length === 0 && !adding && keys.length === 0 && (
        <EmptyState className="!py-2">No wallets yet.</EmptyState>
      )}

      {keys.map((k, i) => (
        <StateLine
          key={i}
          className="py-1"
          more={<>Next receive 0/{k.last_receive_used + 1}. The addresses come from the Ledger account key — each one is derived before it is used.</>}
        >
          Ledger key · {k.registered} registered · {k.last_scanned_at ? `scanned ${fmtRelative(k.last_scanned_at)}` : 'not scanned'}
        </StateLine>
      ))}

      {current.length > 0 && <ul>{current.map((w) => <WalletRow key={w.id} w={w} />)}</ul>}

      {retired.length > 0 && (
        <details className="mt-3">
          <summary className="tap-44 cursor-pointer py-2 text-meta text-tg-hint max-lg:py-3">{retired.length} retired — still watched</summary>
          <ul className="opacity-70">{retired.map((w) => <WalletRow key={w.id} w={w} />)}</ul>
        </details>
      )}
      {archived.length > 0 && (
        <details className="mt-3">
          <summary className="tap-44 cursor-pointer py-2 text-meta text-tg-hint max-lg:py-3">{archived.length} archived — not watched</summary>
          <ul className="opacity-70">{archived.map((w) => <WalletRow key={w.id} w={w} />)}</ul>
        </details>
      )}
      <p className="mt-3">
        <Link to="/wallets" className="btn-link inline-flex min-h-11 items-center text-body lg:min-h-10">Wallet register →</Link>
      </p>
    </Panel>
  );
}

function WalletRow({ w }: { w: PartyWallet }) {
  const state = walletState(w.status);
  return (
    <li className="border-b border-stroke-soft py-3 last:border-b-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-body font-medium">{w.name?.full ?? <>{w.chain}{w.label && ` · ${w.label}`}</>}</span>
        {w.status !== 'active' && <Pill tone={state.tone}>{state.word}</Pill>}
      </div>
      <code className="mt-1 block break-all font-mono text-meta text-tg-hint">{w.address}</code>
      {w.risk_score != null && (
        <div className="mt-1 text-meta text-tg-hint">
          KYT risk <span className="font-mono tabular text-tg-text">{w.risk_score}</span>
          {w.risk_checked_at && ` · ${fmtRelative(w.risk_checked_at)}`}
        </div>
      )}
      {w.status === 'archived' && w.retired_at && (
        <div className="mt-1 text-meta text-tg-hint">
          Retired {fmtRelative(w.retired_at)} · {chainIsRead(w.chain) ? 'transfers here still get matched' : watchWords(w.status, w.chain)}
        </div>
      )}
      {w.status === 'unwatched' && (
        <div className="mt-1 text-meta text-tg-hint">
          Archived{w.unwatched_at ? ` ${fmtRelative(w.unwatched_at)}` : ''} · no longer watched — money sent here is not seen
        </div>
      )}
    </li>
  );
}
